import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MultilingualBtn from '../../local/MultilingualBtn';
import navStyles from './navbar.module.scss';

export default function Navbar() {
    const { t, i18n } = useTranslation();
    const [isMobile, setIsMobile] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        // Check initially
        checkMobile();

        // Add event listener
        window.addEventListener('resize', checkMobile);

        // Cleanup
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    return (
        <header className={`${navStyles.header} ${i18n.language === 'ar' ? navStyles.header_ar : ''}`}>
            <Link to="/home" className={navStyles.logo_link} onClick={closeMenu}>
                <img
                    src="../images/logo/ArabTechLogo/ArabtechLogo.svg"
                    alt="Arabtech logo"
                    className={navStyles.logo}
                />
            </Link>

            {/* Desktop Navigation */}
            {!isMobile && (
                <>
                    <DesktopNav t={t} i18n={i18n} />
                    <div className={navStyles.nav_right_side}>
                        <MultilingualBtn t={t} i18n={i18n} />
                    </div>
                </>
            )}

            {/* Mobile Navigation */}
            {isMobile && (
                <>
                    <button
                        className={`${navStyles.hamburger} ${isMenuOpen ? navStyles.hamburger_active : ''}`}
                        onClick={toggleMenu}
                        aria-label="Toggle menu"
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>

                    <div className={`${navStyles.mobile_menu} ${isMenuOpen ? navStyles.mobile_menu_active : ''}`}>
                        <MobileNav t={t} i18n={i18n} onLinkClick={closeMenu} />
                        <div className={navStyles.mobile_nav_right}>
                            <MultilingualBtn t={t} i18n={i18n} />
                        </div>
                    </div>

                    {isMenuOpen && (
                        <div className={navStyles.overlay} onClick={closeMenu}></div>
                    )}
                </>
            )}
        </header>
    );
}

// Desktop Navigation Component
function DesktopNav({ t, i18n }) {
    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDropdownToggle = (dropdownName) => {
        setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
    };

    return (
        <nav className={navStyles.main_nav}>
            <ul className={`${navStyles.main_nav_list} ${i18n.language === 'ar' ? navStyles.main_nav_list_ar : ''}`}>
                <li>
                    <Link to="/home" className={navStyles.main_nav_link}>
                        {t('home')}
                    </Link>
                </li>
                <li>
                    <Link to="/about" className={navStyles.main_nav_link}>
                        {t('about')}
                    </Link>
                </li>

                {/* Partnership Dropdown */}
                <li
                    className={navStyles.dropdown}
                    ref={dropdownRef}
                    onMouseEnter={() => setOpenDropdown('partnership')}
                    onMouseLeave={() => setOpenDropdown(null)}
                >
                    <button
                        className={`${navStyles.main_nav_link} ${navStyles.dropdown_toggle}`}
                        onClick={() => handleDropdownToggle('partnership')}
                        aria-expanded={openDropdown === 'partnership'}
                    >
                        {t('partnership')}
                        <span className={`${navStyles.dropdown_arrow} ${openDropdown === 'partnership' ? navStyles.dropdown_arrow_open : ''}`}>
                            {/* ▼ */}
                        </span>
                    </button>

                    {openDropdown === 'partnership' && (
                        <ul className={navStyles.dropdown_menu}>
                            <li>
                                <Link to="/unitedecm" className={navStyles.dropdown_link}>
                                    {t('unitedecm')}
                                </Link>
                            </li>
                        </ul>
                    )}
                </li>

                <li>
                    <Link to="/projects" className={navStyles.main_nav_link}>
                        {t('projects')}
                    </Link>
                </li>
                <li>
                    <Link to="/documents" className={navStyles.main_nav_link}>
                        {t('documents')}
                    </Link>
                </li>
                <li>
                    <Link to="/contact" className={navStyles.main_nav_link}>
                        {t('contact')}
                    </Link>
                </li>
            </ul>
        </nav>
    );
}

// Mobile Navigation Component
function MobileNav({ t, i18n, onLinkClick }) {
    const [openDropdown, setOpenDropdown] = useState(null);

    const handleDropdownToggle = (dropdownName) => {
        setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
    };

    return (
        <nav className={navStyles.main_nav_mobile}>
            <ul className={navStyles.main_nav_list}>
                <li>
                    <Link to="/home" className={navStyles.main_nav_link} onClick={onLinkClick}>
                        {t('home')}
                    </Link>
                </li>
                <li>
                    <Link to="/about" className={navStyles.main_nav_link} onClick={onLinkClick}>
                        {t('about')}
                    </Link>
                </li>

                {/* Partnership Dropdown */}
                <li className={navStyles.dropdown}>
                    <button
                        className={`${navStyles.main_nav_link} ${navStyles.dropdown_toggle}`}
                        onClick={() => handleDropdownToggle('partnership')}
                        aria-expanded={openDropdown === 'partnership'}
                    >
                        {t('partnership')}
                        <span className={`${navStyles.dropdown_arrow} ${openDropdown === 'partnership' ? navStyles.dropdown_arrow_open : ''}`}>
                            ▼
                        </span>
                    </button>

                    {openDropdown === 'partnership' && (
                        <ul className={navStyles.dropdown_menu}>
                            <li>
                                <Link
                                    to="/partnership/united"
                                    className={navStyles.dropdown_link}
                                    onClick={onLinkClick}
                                >
                                    {t('united')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/partnership/rse"
                                    className={navStyles.dropdown_link}
                                    onClick={onLinkClick}
                                >
                                    {t('rse')}
                                </Link>
                            </li>
                        </ul>
                    )}
                </li>

                <li>
                    <Link to="/projects" className={navStyles.main_nav_link} onClick={onLinkClick}>
                        {t('projects')}
                    </Link>
                </li>
                <li>
                    <Link to="/documents" className={navStyles.main_nav_link} onClick={onLinkClick}>
                        {t('documents')}
                    </Link>
                </li>
                <li>
                    <Link to="/contact" className={navStyles.main_nav_link} onClick={onLinkClick}>
                        {t('contact')}
                    </Link>
                </li>
            </ul>
        </nav>
    );
}